<script setup>
// Aba "Contrato": gera o contrato a partir de uma proposta do negócio, lista
// os contratos com o status da assinatura e manda o link (/ct/:token) na
// conversa. O texto é congelado no backend; aqui só se cria, envia e cancela.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ContractsAPI from 'dashboard/api/contracts';
import ProposalsAPI from 'dashboard/api/proposals';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContractIssuerForm from './contracts/ContractIssuerForm.vue';
import ContractNewForm from './contracts/ContractNewForm.vue';
import {
  formatBRL,
  formatDocument,
  formatDateTime,
} from './contracts/contractFormat';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const availability = ref(null);
const contracts = ref([]);
const proposals = ref([]);
const isLoading = ref(true);
const loadFailed = ref(false);
const showForm = ref(false);
const showIssuer = ref(false);
const busyId = ref(null);

const issuerComplete = computed(() => availability.value?.issuer_complete);
const canEditIssuer = computed(() => availability.value?.can_edit_issuer);
const clientName = computed(() => props.conversation?.meta?.sender?.name || '');

// O trilho à esquerda de cada contrato diz o estado sem precisar ler o chip.
const STATUS_STYLE = {
  draft: { rail: 'bg-n-slate-7', chip: 'bg-n-slate-3 text-n-slate-11' },
  sent: { rail: 'bg-n-amber-9', chip: 'bg-n-amber-3 text-n-amber-11' },
  signed: { rail: 'bg-n-teal-9', chip: 'bg-n-teal-3 text-n-teal-11' },
  canceled: { rail: 'bg-n-ruby-7', chip: 'bg-n-ruby-3 text-n-ruby-11' },
};
const statusStyle = contract => STATUS_STYLE[contract.status];
const isOpen = contract => ['draft', 'sent'].includes(contract.status);

const load = async () => {
  isLoading.value = true;
  loadFailed.value = false;
  try {
    const [{ data: avail }, { data: list }, { data: proposalList }] =
      await Promise.all([
        ContractsAPI.availability(),
        ContractsAPI.list(props.conversation.id),
        ProposalsAPI.list(props.conversation.id),
      ]);
    availability.value = avail;
    contracts.value = list;
    proposals.value = proposalList;
    showIssuer.value = !avail.issuer_complete && avail.can_edit_issuer;
  } catch {
    loadFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

const onIssuerSaved = async () => {
  showIssuer.value = false;
  const { data } = await ContractsAPI.availability();
  availability.value = data;
};

const onCreated = async contract => {
  contracts.value = [contract, ...contracts.value];
  showForm.value = false;
  await copyTextToClipboard(contract.public_url);
  useAlert(t('DEALS.CONTRACTS.ALERTS.CREATED'));
};

const replace = updated => {
  contracts.value = contracts.value.map(c =>
    c.id === updated.id ? updated : c
  );
};

const run = async (contract, action) => {
  busyId.value = contract.id;
  try {
    await action();
  } catch {
    useAlert(t('DEALS.CONTRACTS.ALERTS.ERROR'));
  } finally {
    busyId.value = null;
  }
};

const copyLink = async contract => {
  await copyTextToClipboard(contract.public_url);
  useAlert(t('DEALS.CONTRACTS.ALERTS.COPIED'));
};

const openLink = contract => {
  window.open(contract.public_url, '_blank', 'noopener');
};

const sendInConversation = contract =>
  run(contract, async () => {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.conversation.id,
      message: `${t('DEALS.CONTRACTS.MESSAGE_TEXT')}\n${contract.public_url}`,
    });
    const { data } = await ContractsAPI.markSent(contract.id);
    replace(data);
    useAlert(t('DEALS.CONTRACTS.ALERTS.SENT'));
  });

const cancelContract = contract => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('DEALS.CONTRACTS.CONFIRM_CANCEL'))) return;
  run(contract, async () => {
    const { data } = await ContractsAPI.cancelContract(contract.id);
    replace(data);
    useAlert(t('DEALS.CONTRACTS.ALERTS.CANCELED'));
  });
};

const deleteContract = contract => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('DEALS.CONTRACTS.CONFIRM_DELETE'))) return;
  run(contract, async () => {
    await ContractsAPI.deleteContract(contract.id);
    contracts.value = contracts.value.filter(c => c.id !== contract.id);
    useAlert(t('DEALS.CONTRACTS.ALERTS.DELETED'));
  });
};

const createdLine = contract =>
  contract.created_by
    ? t('DEALS.CONTRACTS.ITEM.CREATED', {
        date: formatDateTime(contract.created_at),
        user: contract.created_by,
      })
    : t('DEALS.CONTRACTS.ITEM.CREATED_NO_USER', {
        date: formatDateTime(contract.created_at),
      });
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <div
      v-if="isLoading"
      class="flex items-center gap-2 text-sm text-n-slate-11"
    >
      <Spinner class="size-4" />
      {{ t('DEALS.CONTRACTS.LOADING') }}
    </div>

    <div
      v-else-if="loadFailed"
      class="flex flex-col items-start gap-3 text-sm text-n-slate-11"
    >
      {{ t('DEALS.CONTRACTS.LOAD_ERROR') }}
      <Button
        size="sm"
        variant="faded"
        color="slate"
        :label="t('DEALS.CONTRACTS.RETRY')"
        @click="load"
      />
    </div>

    <template v-else>
      <header class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('DEALS.CONTRACTS.TITLE') }}
          </h3>
          <p class="mt-0.5 text-xs leading-relaxed text-n-slate-11">
            {{ t('DEALS.CONTRACTS.SUBTITLE') }}
          </p>
        </div>
        <Button
          v-if="issuerComplete && proposals.length"
          size="sm"
          :variant="showForm ? 'faded' : 'solid'"
          :color="showForm ? 'slate' : 'blue'"
          :icon="showForm ? 'i-lucide-x' : 'i-lucide-file-signature'"
          :label="
            showForm
              ? t('DEALS.CONTRACTS.CLOSE_FORM')
              : t('DEALS.CONTRACTS.NEW')
          "
          @click="showForm = !showForm"
        />
      </header>

      <p
        v-if="!issuerComplete && !canEditIssuer"
        class="rounded-lg bg-n-amber-3 px-3 py-2 text-sm text-n-amber-11"
      >
        {{ t('DEALS.CONTRACTS.ISSUER.MISSING_AGENT') }}
      </p>

      <ContractIssuerForm v-if="showIssuer" @saved="onIssuerSaved" />

      <p
        v-if="issuerComplete && !proposals.length"
        class="rounded-lg border border-dashed border-n-weak px-3 py-3 text-sm text-n-slate-11"
      >
        {{ t('DEALS.CONTRACTS.NO_PROPOSAL') }}
      </p>

      <section
        v-if="showForm"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <ContractNewForm
          :conversation-id="conversation.id"
          :proposals="proposals"
          :default-client-name="clientName"
          @created="onCreated"
        />
      </section>

      <ul v-if="contracts.length" class="flex flex-col gap-2">
        <li
          v-for="contract in contracts"
          :key="contract.id"
          class="flex overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
          :class="{ 'opacity-70': contract.status === 'canceled' }"
        >
          <span
            class="w-1 shrink-0"
            :class="statusStyle(contract).rail"
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1 p-3">
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs text-n-slate-11">
                {{ t('DEALS.CONTRACTS.ITEM.NUMBER', { id: contract.id }) }}
              </span>
              <span
                class="rounded-full px-2 py-0.5 text-xs font-medium"
                :class="statusStyle(contract).chip"
              >
                {{ t(`DEALS.CONTRACTS.STATUS.${contract.status}`) }}
              </span>
            </div>
            <p class="mt-1 truncate text-sm font-semibold text-n-slate-12">
              {{ contract.contratante_nome }}
            </p>
            <p class="flex flex-wrap gap-x-3 text-xs text-n-slate-11">
              <span>{{ formatDocument(contract.contratante_documento) }}</span>
              <span v-if="contract.total_mensal">
                {{
                  t('DEALS.CONTRACTS.ITEM.MONTHLY', {
                    total: formatBRL(contract.total_mensal),
                  })
                }}
              </span>
            </p>
            <p
              v-if="contract.status === 'signed'"
              class="mt-2 text-xs font-medium text-n-teal-11"
            >
              {{
                t('DEALS.CONTRACTS.ITEM.SIGNED', {
                  name: contract.signed_name,
                  date: formatDateTime(contract.signed_at),
                })
              }}
            </p>
            <p v-else class="mt-2 text-xs text-n-slate-10">
              {{ createdLine(contract) }}
            </p>

            <div class="mt-3 flex flex-wrap gap-1.5">
              <Button
                size="xs"
                variant="faded"
                color="slate"
                icon="i-lucide-link"
                :label="t('DEALS.CONTRACTS.ACTIONS.COPY')"
                @click="copyLink(contract)"
              />
              <Button
                size="xs"
                variant="faded"
                color="slate"
                icon="i-lucide-external-link"
                :label="t('DEALS.CONTRACTS.ACTIONS.OPEN')"
                @click="openLink(contract)"
              />
              <template v-if="isOpen(contract)">
                <Button
                  size="xs"
                  variant="faded"
                  color="blue"
                  icon="i-lucide-send"
                  :label="t('DEALS.CONTRACTS.ACTIONS.SEND')"
                  :is-loading="busyId === contract.id"
                  @click="sendInConversation(contract)"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  color="ruby"
                  :label="t('DEALS.CONTRACTS.ACTIONS.CANCEL')"
                  :disabled="busyId === contract.id"
                  @click="cancelContract(contract)"
                />
              </template>
              <Button
                v-if="contract.status !== 'signed'"
                size="xs"
                variant="ghost"
                color="ruby"
                icon="i-lucide-trash-2"
                :label="t('DEALS.CONTRACTS.ACTIONS.DELETE')"
                :disabled="busyId === contract.id"
                @click="deleteContract(contract)"
              />
            </div>
          </div>
        </li>
      </ul>

      <p
        v-else-if="issuerComplete && proposals.length && !showForm"
        class="text-sm text-n-slate-11"
      >
        {{ t('DEALS.CONTRACTS.EMPTY') }}
      </p>

      <Button
        v-if="issuerComplete && canEditIssuer && !showIssuer"
        variant="link"
        color="slate"
        size="xs"
        class="self-start"
        :label="t('DEALS.CONTRACTS.ISSUER.EDIT')"
        @click="showIssuer = true"
      />
    </template>
  </div>
</template>
